import {
  Delete as DeleteIcon,
  Email as EmailIcon,
  QrCode as QrCodeIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { Avatar, Box, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useToast } from "~hooks/useToast";
import { useDispatch } from "~hooks/useTypedRedux";
import { Route } from "~services/router/Route";
import { EntityAvatar } from "~ui/atoms/EntityAvatar/EntityAvatar";
import { AccessibilityChip } from "~ui/molecules/AccessibilityChip/AccessibilityChip";
import { MembershipChip } from "~ui/molecules/MembershipChip/MembershipChip";
import { RoomFormModal } from "~ui/molecules/RoomFormModal/RoomFormModal";
import { SearchableChip } from "~ui/molecules/SearchableChip/SearchableChip";
import { BulkEmailModal } from "~ui/organisms/BulkEmail/BulkEmailModal";
import { makeBubbleAvatarUrl, makeRoomUrl } from "~utils/url";

import { pageDialogOpen } from "../../../state/app";

// Use the same room type as the template for consistency
export interface RoomHeaderProps {
  room: {
    id: string;
    name: string;
    isPublic: boolean;
    isSearchable: boolean;
    bubbleId?: string | null;
    userIsFacilitator: boolean;
    userIsOwner: boolean;
    userIsMember: boolean;
    userIsPending: boolean;
    userIsAdmin: boolean;
    description: string;
  };
  members?: {
    id: string;
    name: string;
    email: string;
    roles: string[];
    isFacilitator: boolean;
  }[];
  imageUrl?: string;
  status?: "idle" | "loading" | "error" | "success";
  onImageChange?: (file: File) => void;
  onJoinRoom?: () => Promise<void>;
  onLeaveRoom?: () => Promise<void>;
  onCancelRequest?: () => Promise<void>;
  onUpdateRoom?: (values: { description: string; isPublic: boolean; isSearchable: boolean; name: string }) => void;
  bubbleSelectItems?: { id: string; value: string }[];
}

const tShareTooltip = "Share room link";
const tQRTooltip = "View check-in QR code";
const tDeleteTooltip = "Delete room";
const tEmailTooltip = "Send email to room members";

export const RoomHeader = ({
  room,
  members = [],
  imageUrl,
  status = "idle",
  onImageChange,
  onJoinRoom,
  onLeaveRoom,
  onCancelRequest,
  onUpdateRoom,
  bubbleSelectItems = [],
}: RoomHeaderProps) => {
  const toast = useToast();
  const dispatch = useDispatch();
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const { userIsFacilitator, userIsOwner, userIsMember, userIsPending, isPublic } = room;
  const canEdit = userIsFacilitator || userIsOwner;
  const sharableRoomLink = makeRoomUrl(room.id);
  const bubbleAvatarUrl = room.bubbleId ? makeBubbleAvatarUrl(room.bubbleId) : null;

  const canLeave = userIsMember && !userIsOwner;

  const handleShareClick = () => {
    navigator.clipboard.writeText(sharableRoomLink);
    toast.success("Room link copied to clipboard!");
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(
      pageDialogOpen({
        contentProps: { podName: room.name, roomId: room.id },
        contentType: "delete-pod",
      }),
    );
  };

  const handleEmailClick = () => {
    setIsEmailModalOpen(true);
  };

  const handleEmailClose = () => {
    setIsEmailModalOpen(false);
  };

  const handleEmailSend = async () => {
    // The modal's hook will handle the actual sending and toasts
    setIsEmailModalOpen(false);
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Main Room Header */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          mb: 3,
          p: 3,
          position: "relative",
        }}
      >
        {/* Share Button - Upper Right */}
        <Box sx={{ position: "absolute", right: 16, top: 16 }}>
          <Tooltip title={tShareTooltip}>
            <IconButton
              onClick={handleShareClick}
              size="small"
              aria-label="Share room link"
              sx={{
                "&:hover": {
                  backgroundColor: "action.selected",
                },
                backgroundColor: "action.hover",
              }}
            >
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Stack direction={{ md: "row", xs: "column" }} spacing={3} alignItems={{ md: "flex-start", xs: "center" }}>
          {/* Room Avatar with Organization Badge */}
          <Box
            sx={{
              display: "inline-block",
              mb: { md: 0, xs: 2 },
              position: "relative",
            }}
          >
            <EntityAvatar
              entityName={room.name}
              imageUrl={imageUrl}
              canEdit={canEdit}
              size="large"
              status={status}
              onImageChange={onImageChange}
              aria-label="Change room picture"
            />

            {/* Organization Badge */}
            {bubbleAvatarUrl && (
              <Avatar
                src={bubbleAvatarUrl}
                sx={{
                  border: "3px solid white",
                  bottom: -4,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  height: 32,
                  position: "absolute",
                  right: -4,
                  width: 32,
                  zIndex: 1,
                }}
                role="img"
                aria-label="Organization badge"
              />
            )}
          </Box>

          {/* Room Info */}
          <Stack
            direction="column"
            spacing={1}
            sx={{
              alignItems: { md: "flex-start", xs: "center" },
              flex: 1,
              width: { md: "auto", xs: "100%" },
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              fontWeight="bold"
              sx={{
                color: "text.primary",
                lineHeight: 1.2,
                textAlign: { md: "left", xs: "center" },
              }}
            >
              {room.name}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              justifyContent={{ md: "flex-start", xs: "center" }}
              sx={{ width: "100%" }}
            >
              <MembershipChip
                isOwner={userIsOwner}
                isFacilitator={userIsFacilitator}
                isMember={userIsMember}
                isPending={userIsPending}
                isPublic={isPublic}
                onJoin={onJoinRoom}
                onLeave={canLeave ? onLeaveRoom : undefined}
                onCancel={onCancelRequest}
              />
              <AccessibilityChip isPublic={room.isPublic} />
              <SearchableChip isSearchable={room.isSearchable} />
            </Stack>

            {/* Action Buttons */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent={{ md: "flex-start", xs: "center" }}
              sx={{ mt: 2, width: "100%" }}
            >
              {canEdit && (
                <>
                  <Tooltip title={tEmailTooltip}>
                    <IconButton
                      onClick={handleEmailClick}
                      size="small"
                      aria-label="Send email"
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.selected",
                        },
                        backgroundColor: "action.hover",
                      }}
                    >
                      <EmailIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={tQRTooltip}>
                    <IconButton
                      component={Link}
                      to={Route.podQr({ roomId: room.id })}
                      target="_blank"
                      size="small"
                      aria-label="View check-in QR code"
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.selected",
                        },
                        backgroundColor: "action.hover",
                      }}
                    >
                      <QrCodeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {onUpdateRoom && (
                    <RoomFormModal
                      mode="edit"
                      onSubmit={onUpdateRoom}
                      bubbleSelectItems={bubbleSelectItems}
                      initialValues={{
                        bubbleId: room.bubbleId || undefined,
                        description: room.description,
                        isPublic: room.isPublic,
                        isSearchable: room.isSearchable,
                        name: room.name,
                      }}
                    />
                  )}

                  <Tooltip title={tDeleteTooltip}>
                    <IconButton
                      onClick={handleDeleteClick}
                      size="small"
                      aria-label="Delete room"
                      sx={{
                        "&:hover": {
                          backgroundColor: "error.main",
                          color: "error.contrastText",
                        },
                        backgroundColor: "error.light",
                        color: "error.dark",
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {/* Bulk Email Modal */}
      <BulkEmailModal
        isOpen={isEmailModalOpen}
        onClose={handleEmailClose}
        onSend={handleEmailSend}
        contextData={{
          availableRoles: Array.from(new Set([...members.flatMap((member) => member.roles), "Facilitator", "Member"])),
          availableUsers: members.map((member) => ({
            email: member.email,
            id: member.id,
            name: member.name,
            roles: [...member.roles, member.isFacilitator ? "Facilitator" : "Member"],
          })),
          bubbleId: room.bubbleId || "",
        }}
      />
    </Box>
  );
};
